import { Typography } from '@mui/material';
import { Link, LinkOptions } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { cn } from '../utils';

export type LabeledLink = LinkOptions & { label: string };
type LabeledViewChange = {
  label: string;
  onClick: () => void;
  isActive: boolean;
};

function isLabeledLink(
  item: LabeledLink | LabeledViewChange,
): item is LabeledLink {
  return (
    typeof item === 'object' &&
    item !== null &&
    'to' in item &&
    typeof (item as LabeledLink).to === 'string'
  );
}

type PageHeaderProps = {
  title: string;
  rightSlot?: ReactNode;
  bottomSlot?: ReactNode;
  navigationLinks?: (LabeledLink | LabeledViewChange)[];
};

export function PageHeader({
  title,
  rightSlot,
  bottomSlot,
  navigationLinks,
}: PageHeaderProps) {
  const hasBottomContent = Boolean(bottomSlot || navigationLinks);
  return (
    <div className={`w-full flex flex-col px-6 pt-6 border-b`}>
      <div className="flex w-full">
        <h1 className="text-2xl font-bold flex-grow mt-2">{title}</h1>
        <div className="flex gap-1">{rightSlot}</div>
      </div>

      {hasBottomContent ? (
        <div className="page-header-nav mt-1 inline-flex whitespace-nowrap">
          {bottomSlot && <div className="pb-4 mr-4">{bottomSlot}</div>}
          {navigationLinks && (
            <NavigationLinks navigationLinks={navigationLinks} />
          )}
        </div>
      ) : (
        <div className="pt-6"></div>
      )}
    </div>
  );
}

const NavigationLinks = ({
  navigationLinks,
}: {
  navigationLinks: (LabeledLink | LabeledViewChange)[];
}) => {
  const sharedStyling = {
    className: 'text-md cursor-pointer pb-2 h-full flex items-center mr-4 px-1',
    inactiveProps: {
      className: 'border-b-2 border-white text-gray-500 pb-2',
    },
    activeProps: {
      className: 'border-b-2 text-black border-black pb-2',
      // Route links, not ARIA tabs: the active destination is conveyed
      // with aria-current (no tablist/tab roles — they would require
      // arrow-key roving focus to be valid).
      'aria-current': 'page' as const,
    },
  };
  // for each item in the navigationLinks array, if it is a LabeledLink, render it as a link, otherwise render it as a button
  return navigationLinks.map((item, i) => {
    if (isLabeledLink(item)) {
      const { label, ...linkOpts } = item;
      return (
        <Link key={i} {...linkOpts} {...sharedStyling}>
          <Typography variant="body2">{label}</Typography>
        </Link>
      );
    }

    const { label, onClick } = item;
    return (
      <Typography
        key={i}
        component="button"
        onClick={onClick}
        className={cn(
          'bg-transparent px-1',
          sharedStyling.className,
          item.isActive
            ? sharedStyling.activeProps.className
            : sharedStyling.inactiveProps.className,
        )}
        variant="body2"
      >
        {label}
      </Typography>
    );
  });
};
